import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Pressable, Text, TextInput, View } from 'react-native'
import styled from 'styled-components/native'
import { useDropzone } from 'react-dropzone'
import { Feather } from '../../../../components/icon/vectorIcons'
import { BlockComponentProps } from '../../../../doclets/types'
import {
  MediaPlayer,
  mediaBlockStyle,
  mediaCaptionStyle
} from '../../../../doclets/blocks/media/MediaBlock'
import InlineTextInput from '../../../../doclets/InlineTextInput'
import { useDocletAssets } from '../../../../doclets/DocletAssetsContext'
import { playableMedia } from '../../../../doclets/blocks/media/playable'
import useTranslation from '../../../../hooks/useTranslation'
import { CLOUDINARY_NAME } from '../../cloudinary'
import { formatBytesToMB, formatStringWithArgs } from '../../../../utils/format'
import {
  getVideoDetails,
  getVideoDetailsWithStatus
} from '../../../../utils/uploadToVimeo'
import useMediaUploader from '../../../media/components/useMediaUploader'
import useConfigQuery from '../../../media/queries/useConfigQuery'
import useHostMediaUpdatedSubscription from '../../../media/subscriptions/useHostMediaUpdatedSubscription'
import useCreateHostMediaMutation from '../../../media/mutations/useCreateHostMediaMutation'
import { useRecordHostMedia } from '../hostMediaRecords'
import { builderColors, builderRadii } from '../builderColors'
import {
  HostMediaLike,
  isAwaitingVideo,
  audioSizeLimitExceeded,
  isVimeoVideoGone,
  otherCloudinaryAccount,
  propsFromHostMedia,
  readVimeoState
} from './mediaEditing'

// How often to ask Vimeo about a video that's still transcoding
const VIMEO_POLL_MS = 10000
// How many times to look for a real thumbnail once a video is ready
const THUMBNAIL_TRIES = 6

/**
 * Builder editor for the Media block. Until media is added (or while
 * replacing it) it offers a drop zone and a field for a Vimeo or Cloudinary
 * link; both go through MI6's media uploader, so video lands on Vimeo and
 * audio on Cloudinary, as in the Documents editor. The caption is edited
 * inline under the player.
 *
 * The page plays the block's host media record, not a copy of the file link
 * (playableMedia). A new video has no playable file until Vimeo finishes
 * transcoding; the data-extraction job then updates the record (every minute
 * where it runs; not locally). While the builder is open, the block also asks
 * Vimeo every few seconds and, once the video is ready, brings the record up
 * to date itself, so the result is the same either way. A video Vimeo
 * couldn't process, or that's been deleted or made private, is marked failed
 * on the record the same way: checking stops, the block asks for a
 * replacement and the page leaves it out. Updates reach the canvas through
 * the hostMediaUpdated subscription.
 */
const MediaBlockEditor = ({
  block,
  onChange,
  onFocus,
  selected = false
}: BlockComponentProps<'media'>) => {
  const { t } = useTranslation()
  const { documentFileSizeLimit, audioFileSizeLimit } = useConfigQuery()
  const { hostMedia: records } = useDocletAssets()
  const recordHostMedia = useRecordHostMedia()
  const createHostMedia = useCreateHostMediaMutation()
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [replacing, setReplacing] = useState(false)
  const [link, setLink] = useState('')
  const [checkingLink, setCheckingLink] = useState(false)
  // The uploader keeps its last error until its next upload; this says
  // whether that error is still news (cleared by a success, another try or
  // Cancel)
  const [uploadFailed, setUploadFailed] = useState(false)
  const { kind, caption, host, hostId, poster } = block.props
  const { src } = playableMedia(block.props, records)

  // Uploads and Vimeo finish later: apply them to the props as they are then
  const latest = useRef({ props: block.props, onChange })
  latest.current = { props: block.props, onChange }
  // Tidy the caption when the block is deselected, as the Image block does: a
  // spaces-only caption counts as none (so the "Add a caption" prompt comes
  // back), and a real one loses its outer spaces, as the page shows it. Not
  // while typing, so a trailing space doesn't vanish mid-word.
  const wasSelected = useRef(selected)
  useEffect(() => {
    if (wasSelected.current && !selected) {
      const { props, onChange: save } = latest.current
      const tidy = props.caption.trim()
      if (tidy !== props.caption) save?.({ ...props, caption: tidy })
    }
    wasSelected.current = selected
  }, [selected])

  const apply = useCallback(
    (media: HostMediaLike | null) => {
      // The uploader also reports an in-progress placeholder (no id) and null
      if (!media?.id) return
      recordHostMedia(media)
      latest.current.onChange?.(propsFromHostMedia(latest.current.props, media))
      setReplacing(false)
      setLink('')
      setError(null)
      setUploadFailed(false)
    },
    [recordHostMedia]
  )

  const {
    uploadMedia,
    error: uploadError,
    validateUploadedMediaUrl
  } = useMediaUploader(
    apply,
    documentFileSizeLimit,
    audioFileSizeLimit,
    [uploading, setUploading],
    // The uploader's types use the Number wrapper; this keeps a plain number
    [progress, value => setProgress(value === null ? null : Number(value))]
  )

  const awaiting = isAwaitingVideo(block.props, records)

  // The record changed: Vimeo finished, as seen by data-extraction or by
  // this or another open builder
  useHostMediaUpdatedSubscription(
    awaiting ? block.props.hostMediaId : null,
    ({ data }) => recordHostMedia(data?.data?.hostMediaUpdated)
  )

  // Ask Vimeo directly until the video is ready: show its thumbnail early,
  // and once it's done, update the host media record (not the block)
  const awaitingVimeo = awaiting && host === 'vimeo' && !!hostId
  useEffect(() => {
    if (!awaitingVimeo || !hostId) return
    let active = true
    let timer: ReturnType<typeof setTimeout>
    // Upserts the record by hostId, as data-extraction does; announces the
    // change on hostMediaUpdated, so the canvas and other builders follow
    const updateRecord = async (variables: Record<string, unknown>) => {
      const result = await createHostMedia({
        variables: { hostId, host: 'vimeo', type: 'video', ...variables }
      })
      if (active) recordHostMedia(result?.data?.createHostMedia)
    }
    // Shown as failed here straight away; the record is updated too, so the
    // page leaves it out. Checking stops even if that update doesn't go
    // through: asking again wouldn't change Vimeo's answer.
    const markFailed = async () => {
      recordHostMedia({
        id: latest.current.props.hostMediaId,
        url: null,
        transcode: 'error'
      })
      try {
        await updateRecord({ status: 'error', transcode: 'error', url: null })
      } catch {
        // The builder already shows it as failed
      }
    }
    const check = async () => {
      try {
        const { status, ok, details } = await getVideoDetailsWithStatus(hostId)
        if (!active) return
        // Deleted or made private: it won't come back
        if (isVimeoVideoGone(status)) {
          await markFailed()
          return
        }
        if (ok) {
          const state = readVimeoState(details)
          const current = latest.current.props
          if (!current.poster && state.thumbnail) {
            latest.current.onChange?.({ ...current, poster: state.thumbnail })
          }
          if (state.failed) {
            await markFailed()
            return
          }
          if (state.complete) {
            await updateRecord({
              status: 'complete',
              transcode: 'complete',
              url: state.url,
              width: state.width ?? undefined,
              height: state.height ?? undefined,
              duration: state.duration ?? undefined
            })
            return
          }
        }
      } catch {
        // Try again on the next round
      }
      if (active) timer = setTimeout(check, VIMEO_POLL_MS)
    }
    check()
    return () => {
      active = false
      clearTimeout(timer)
    }
  }, [awaitingVimeo, hostId, createHostMedia, recordHostMedia])

  // A finished Vimeo video gets its thumbnail as the poster. Only a real
  // frame is saved: Vimeo shows a generic placeholder until it has made one
  // (and a saved poster is never looked up again), so until then this asks
  // again a few times. If Vimeo can't be reached at all, vumbnail.com
  // provides one instead.
  useEffect(() => {
    if (kind !== 'video' || host !== 'vimeo' || !src || poster || !hostId) {
      return
    }
    let active = true
    let timer: ReturnType<typeof setTimeout>
    const savePoster = (thumbnail: string) => {
      if (!active || latest.current.props.hostId !== hostId) return
      latest.current.onChange?.({ ...latest.current.props, poster: thumbnail })
    }
    const lookUp = async (triesLeft: number) => {
      let thumbnail: string | null
      try {
        thumbnail = readVimeoState(await getVideoDetails(hostId)).thumbnail
      } catch {
        savePoster(`https://vumbnail.com/${hostId}.png`)
        return
      }
      if (thumbnail) savePoster(thumbnail)
      else if (active && triesLeft > 1) {
        timer = setTimeout(() => lookUp(triesLeft - 1), VIMEO_POLL_MS)
      }
    }
    lookUp(THUMBNAIL_TRIES)
    return () => {
      active = false
      clearTimeout(timer)
    }
  }, [kind, host, hostId, src, poster])

  // Any error the uploader reports is an upload that failed: its own size
  // check never fires, as files over the limit are stopped below, so what's
  // left (Vimeo / Cloudinary or network errors) is worth trying again
  useEffect(() => {
    if (uploadError) setUploadFailed(true)
  }, [uploadError])

  // One upload at a time: a second file dropped (or chosen) mid-upload would
  // race the first, and whichever finished last would be saved. A ref, not
  // state, so two drops in the same tick can't both get through.
  const uploadInFlight = useRef(false)

  const upload = useCallback(
    async (files: File[]) => {
      const file = files[0]
      if (!file || uploadInFlight.current) return
      onFocus?.()
      setUploadFailed(false)
      // Too large for MI6's audio limit: say so, rather than "try again"
      const limit = audioSizeLimitExceeded(file, audioFileSizeLimit)
      if (limit) {
        setError(
          formatStringWithArgs(
            t('forms:exceedFileSize'),
            formatBytesToMB(limit)
          )
        )
        return
      }
      setError(null)
      uploadInFlight.current = true
      try {
        await uploadMedia(file as any)
      } finally {
        uploadInFlight.current = false
      }
    },
    [onFocus, uploadMedia, audioFileSizeLimit, t]
  )

  const addLink = async () => {
    const url = link.trim()
    if (!url) return
    setError(null)
    setUploadFailed(false)
    const account = otherCloudinaryAccount(url, CLOUDINARY_NAME)
    if (account) {
      setError(
        t('doclets:media:linkOtherAccount', {
          account,
          ourAccount: CLOUDINARY_NAME
        })
      )
      return
    }
    setCheckingLink(true)
    // On success the uploader reports the media through apply()
    const [ok] = await validateUploadedMediaUrl(url)
    setCheckingLink(false)
    if (!ok) setError(t('doclets:media:linkInvalid'))
  }

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    onDrop: upload,
    accept: 'audio/*,video/*',
    multiple: false,
    // No drops (or "Drop to upload" overlay) while an upload is running
    disabled: uploading,
    // Clicking selects the block; uploading is by drop or the button
    noClick: true,
    noKeyboard: true
  })
  // Typed loosely, as in FileDropzone: react-dropzone's prop types don't
  // match MI6's React DOM types
  const rootProps: any = getRootProps()
  const inputProps: any = getInputProps()

  const showChooser = !kind || replacing
  const message =
    error ?? (uploadFailed ? t('doclets:media:uploadFailed') : null)

  return (
    <div {...rootProps} style={{ position: 'relative', outline: 'none' }}>
      <input {...inputProps} />
      {showChooser ? (
        <Chooser>
          <Feather name="film" size={24} color={builderColors.mutedText} />
          <ZoneText>{t('doclets:media:dropHere')}</ZoneText>
          <SmallButton
            icon="upload"
            label={t('doclets:media:chooseFile')}
            onPress={() => {
              onFocus?.()
              open()
            }}
          />
          <ZoneText>{t('doclets:media:orLink')}</ZoneText>
          <View style={{ flexDirection: 'row', alignSelf: 'stretch' }}>
            <TextInput
              value={link}
              onChangeText={setLink}
              onFocus={onFocus}
              onSubmitEditing={addLink}
              placeholder="https://vimeo.com/…"
              autoCapitalize="none"
              accessibilityLabel={t('doclets:media:orLink')}
              style={{
                flex: 1,
                minWidth: 0,
                height: 32,
                paddingHorizontal: 8,
                borderWidth: 1,
                borderColor: builderColors.border,
                borderRadius: builderRadii.md,
                backgroundColor: '#FFFFFF',
                fontSize: 13
              }}
            />
            <SmallButton
              label={
                checkingLink
                  ? t('doclets:media:checkingLink')
                  : t('doclets:media:addLink')
              }
              onPress={addLink}
              style={{ marginLeft: 8 }}
            />
          </View>
          {replacing && (
            <SmallButton
              label={t('doclets:media:cancel')}
              onPress={() => {
                setReplacing(false)
                setLink('')
                setError(null)
                setUploadFailed(false)
              }}
              style={{ marginTop: 12 }}
            />
          )}
        </Chooser>
      ) : (
        <View style={mediaBlockStyle}>
          <MediaPlayer
            block={block}
            processingMessage={t('doclets:media:processing')}
            failedMessage={t('doclets:media:failed')}
          />
          {selected && !uploading && (
            <SmallButton
              icon="refresh-cw"
              label={t('doclets:media:replace')}
              onPress={() => setReplacing(true)}
              style={{ alignSelf: 'flex-start', marginTop: 8 }}
            />
          )}
          {(selected || !!caption.trim()) && (
            <InlineTextInput
              value={caption}
              placeholder={t('doclets:media:captionPlaceholder')}
              accessibilityLabel={t('doclets:media:caption')}
              textStyle={mediaCaptionStyle(block)}
              onFocus={onFocus}
              onChangeText={value =>
                onChange?.({ ...block.props, caption: value })
              }
            />
          )}
        </View>
      )}
      {(uploading || isDragActive) && (
        <Overlay>
          <OverlayText>
            {uploading
              ? progress !== null
                ? t('doclets:media:uploading', { progress })
                : t('doclets:media:uploadingNoProgress')
              : t('doclets:media:dropToUpload')}
          </OverlayText>
        </Overlay>
      )}
      {!!message && <ErrorText>{message}</ErrorText>}
    </div>
  )
}

interface SmallButtonProps {
  icon?: React.ComponentProps<typeof Feather>['name']
  label: string
  onPress: () => void
  style?: object
}

const SmallButton = ({ icon, label, onPress, style }: SmallButtonProps) => (
  <Pressable
    accessibilityRole="button"
    onPress={onPress}
    style={[
      {
        flexDirection: 'row',
        alignItems: 'center',
        alignSelf: 'center',
        height: 32,
        paddingHorizontal: 10,
        borderWidth: 1,
        borderColor: builderColors.border,
        borderRadius: builderRadii.md,
        backgroundColor: '#FFFFFF'
      },
      style
    ]}
  >
    {icon && <Feather name={icon} size={14} color={builderColors.text} />}
    <ButtonText style={{ marginLeft: icon ? 6 : 0 }}>{label}</ButtonText>
  </Pressable>
)

const Chooser = styled(View)`
  align-items: center;
  padding: 24px 16px;
  margin-bottom: 16px;
  border-width: 1px;
  border-style: dashed;
  border-color: ${builderColors.border};
  border-radius: ${builderRadii.md}px;
`

const ZoneText = styled(Text)`
  margin: 8px 0 12px;
  font-size: 13px;
  color: ${builderColors.mutedText};
`

const ButtonText = styled(Text)`
  font-size: 12px;
  font-weight: 600;
  color: ${builderColors.text};
`

const Overlay = styled(View)`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  align-items: center;
  justify-content: center;
  border-width: 2px;
  border-style: dashed;
  border-color: ${builderColors.selectedBorder};
  border-radius: ${builderRadii.md}px;
  background-color: rgba(240, 247, 255, 0.85);
`

const OverlayText = styled(Text)`
  font-size: 13px;
  font-weight: 600;
  color: ${builderColors.tag};
`

const ErrorText = styled(Text)`
  margin-bottom: 16px;
  font-size: 12px;
  color: ${builderColors.danger};
`

export default MediaBlockEditor
